import { useCallback, useRef, useState, useEffect } from 'react'
import { isOverlappingOrDuplicateTranscript } from '@/shared/lib/speechTranscriptDedupe'
import { registerActiveMic, unregisterActiveMic } from './micController'

const getSpeechRecognition = (): (new () => SpeechRecognition) | null => {
  if (typeof window === 'undefined') return null
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function isSpeechSupported(): boolean {
  return typeof window !== 'undefined' && !!getSpeechRecognition()
}

export type SpeechToTextMode = 'streaming' | 'onStop'

export interface UseSpeechToTextOptions {
  /** Chamado quando o reconhecimento retorna transcrição. streaming: a cada frase; onStop: só ao parar (texto acumulado). */
  onTranscript: (text: string) => void
  /** Idioma do reconhecimento (padrão pt-BR). */
  lang?: string
  /** Se true, mantém o microfone aberto até o usuário clicar para parar (máx. 10 min). */
  continuous?: boolean
  /** streaming: envia a cada frase. onStop: envia apenas ao parar (padrão para formulários). */
  mode?: SpeechToTextMode
  /** Se true, registra no controlador global (apenas um mic ativo; ao clicar em outro, para o anterior). */
  useController?: boolean
}

const CONTINUOUS_MAX_MS = 10 * 60 * 1000 // 10 minutos

export interface UseSpeechToTextResult {
  isListening: boolean
  error: string | null
  toggle: () => void
  /** Para a gravação e envia o texto (para submit/envio). */
  stop: () => void
  isSupported: boolean
}

/**
 * Hook para Web Speech API (reconhecimento de voz).
 * Usado em TextArea, TextInput e MicButton.
 *
 * Padrão: sem streaming para o campo (mode onStop) e sessão única (continuous false),
 * para evitar duplicação quando o motor reenvia o mesmo trecho como "final".
 */
export function useSpeechToText({
  onTranscript,
  lang = 'pt-BR',
  continuous = false,
  mode = 'onStop',
  useController = true,
}: UseSpeechToTextOptions): UseSpeechToTextResult {
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const hasFinalTranscriptRef = useRef(false)
  const noSpeechInContinuousRef = useRef(false)
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bufferRef = useRef<string[]>([])
  const myStopRef = useRef<() => void | null>(null)
  /** Evita repetir a mesma transcrição (bug do Web Speech API em Chrome/Android). */
  const lastTranscriptRef = useRef<string>('')
  /** Último segmento já enviado ao pai (modo streaming). */
  const lastEmittedSegmentRef = useRef<string>('')

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current)
      maxDurationTimerRef.current = null
    }
  }, [])

  const doStop = useCallback(() => {
    if (useController && myStopRef.current) {
      unregisterActiveMic(myStopRef.current)
      myStopRef.current = null
    }
    clearMaxDurationTimer()
    try {
      recognitionRef.current?.stop()
    } catch {
      // ignore
    }
    recognitionRef.current = null

    if (mode === 'onStop' && bufferRef.current.length > 0) {
      const text = bufferRef.current.join(' ').trim()
      bufferRef.current = []
      if (text) onTranscript(text)
    }

    lastEmittedSegmentRef.current = ''
    setIsListening(false)
  }, [clearMaxDurationTimer, mode, onTranscript, useController])

  const toggle = useCallback(() => {
    const SpeechRecognitionClass = getSpeechRecognition()
    if (!SpeechRecognitionClass) {
      setError('Seu navegador não suporta reconhecimento de voz.')
      return
    }

    if (isListening) {
      doStop()
      return
    }

    if (useController) {
      const stopFn = () => doStop()
      myStopRef.current = stopFn
      registerActiveMic(stopFn)
    }

    setError(null)
    hasFinalTranscriptRef.current = false
    bufferRef.current = []
    lastTranscriptRef.current = ''
    lastEmittedSegmentRef.current = ''

    const recognition = new SpeechRecognitionClass()
    recognition.lang = lang
    recognition.continuous = continuous
    recognition.interimResults = mode === 'streaming'

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const results = event.results
      if (!results.length) return
      const startIdx = typeof (event as { resultIndex?: number }).resultIndex === 'number'
        ? (event as { resultIndex: number }).resultIndex
        : 0
      for (let i = startIdx; i < results.length; i++) {
        const r = results[i]
        if (!r.isFinal) continue
        if (!continuous) hasFinalTranscriptRef.current = true
        const transcript = r[0]?.transcript?.trim()
        if (!transcript) continue
        if (transcript === lastTranscriptRef.current) continue
        lastTranscriptRef.current = transcript
        if (mode === 'streaming') {
          const prevSeg = lastEmittedSegmentRef.current
          if (prevSeg && isOverlappingOrDuplicateTranscript(prevSeg, transcript)) continue
          lastEmittedSegmentRef.current = transcript
          onTranscript(transcript)
        } else {
          const lastBuf = bufferRef.current[bufferRef.current.length - 1]
          if (lastBuf && isOverlappingOrDuplicateTranscript(lastBuf, transcript)) continue
          bufferRef.current.push(transcript)
        }
      }
    }

    recognition.onend = () => {
      if (continuous && noSpeechInContinuousRef.current) {
        noSpeechInContinuousRef.current = false
        try {
          recognition.start()
          return
        } catch {
          // falha ao reiniciar, fecha
        }
      }
      doStop()
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'not-allowed') {
        clearMaxDurationTimer()
        setError('Permissão de microfone negada.')
        doStop()
      } else if (event.error === 'no-speech' && continuous) {
        noSpeechInContinuousRef.current = true
      } else if (event.error === 'no-speech') {
        clearMaxDurationTimer()
        setError('Nenhuma fala detectada.')
        doStop()
      } else {
        clearMaxDurationTimer()
        setError(`Erro: ${event.error}`)
        doStop()
      }
    }

    try {
      recognition.start()
      recognitionRef.current = recognition
      setIsListening(true)
      maxDurationTimerRef.current = setTimeout(() => {
        maxDurationTimerRef.current = null
        doStop()
      }, CONTINUOUS_MAX_MS)
    } catch {
      setError('Não foi possível iniciar o microfone.')
      setIsListening(false)
      if (useController && myStopRef.current) {
        unregisterActiveMic(myStopRef.current)
        myStopRef.current = null
      }
    }
  }, [isListening, onTranscript, lang, continuous, mode, useController, doStop, clearMaxDurationTimer])

  const stop = useCallback(() => {
    if (isListening) doStop()
  }, [isListening, doStop])

  useEffect(() => {
    return () => {
      doStop()
    }
  }, [doStop])

  const isSupported = typeof window !== 'undefined' && !!getSpeechRecognition()

  return { isListening, error, toggle, stop, isSupported }
}
